import React, { useEffect, useMemo, useState } from "react";
import api from "../../api";
import { useNavigate, useParams } from "react-router-dom";

const EditPost = () => {
  const { id: slug } = useParams();
  const navigate = useNavigate();

  const [postId, setPostId] = useState(null);
  const [postData, setPostData] = useState({ title: "", slug: "", description: "", image: "", thumbnail: "" });
  const [existingGallery, setExistingGallery] = useState([]);
  const [removedGalleryIds, setRemovedGalleryIds] = useState([]);
  const [newGalleryFiles, setNewGalleryFiles] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return path.startsWith("/") ? `${process.env.REACT_APP_IMG_URL}${path}` : `${process.env.REACT_APP_IMG_URL}/${path}`;
  };

  const mainImagePreview = useMemo(() => {
    if (postData.image instanceof File) return URL.createObjectURL(postData.image);
    return null;
  }, [postData.image]);

  useEffect(() => {
    return () => { if (mainImagePreview) URL.revokeObjectURL(mainImagePreview); };
  }, [mainImagePreview]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        const [postResponse, categoriesResponse] = await Promise.all([
          api.get(`/posts/show/${slug}`),
          api.get("/categories"),
        ]);

        const post = postResponse.data;
        setPostId(post.id);
        setPostData({ title: post.title || "", slug: post.slug || "", description: post.description || "", image: post.image || "", thumbnail: post.thumbnail || "" });
        setSelectedCategories(Array.isArray(post.categories) ? post.categories : []);
        setExistingGallery(
          Array.isArray(post.postImages)
            ? post.postImages.map((image, index) => {
                if (typeof image === "string") return { id: null, path: image, thumbnail: image, order: index };
                return { id: image.id, path: image.path, thumbnail: image.thumbnail || image.path, order: image.order ?? index };
              })
            : []
        );
        setAllCategories(Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []);
      } catch (err) {
        console.error(err);
        setError("Erreur lors du chargement du post.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPostData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) setPostData((prev) => ({ ...prev, image: file }));
  };

  const handleCategoryChange = (e) => {
    const categoryName = e.target.value;
    setSelectedCategories((prev) =>
      e.target.checked ? [...prev, categoryName] : prev.filter((name) => name !== categoryName)
    );
  };

  const handleNewGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    setNewGalleryFiles((prev) => [...prev, ...files]);
  };

  const moveExistingGalleryImage = (fromIndex, toIndex) => {
    if (submitting) return;
    setExistingGallery((prev) => {
      const updated = [...prev];
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= updated.length || toIndex >= updated.length) return updated;
      const [movedItem] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedItem);
      return updated;
    });
  };

  const moveNewGalleryFile = (fromIndex, toIndex) => {
    if (submitting) return;
    setNewGalleryFiles((prev) => {
      const updated = [...prev];
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= updated.length || toIndex >= updated.length) return updated;
      const [movedItem] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedItem);
      return updated;
    });
  };

  const handleRemoveExisting = (image) => {
    if (submitting) return;
    setExistingGallery((prev) => prev.filter((img) => img !== image));
    if (image.id) setRemovedGalleryIds((prev) => [...prev, image.id]);
  };

  const handleRemoveNewFile = (index) => {
    if (submitting) return;
    setNewGalleryFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !postId) return;
    setError("");
    setSubmitting(true);

    const formData = new FormData();
    formData.append("Title", postData.title);
    formData.append("Slug", postData.slug);
    formData.append("Description", postData.description);
    if (postData.image instanceof File) formData.append("Image", postData.image);
    selectedCategories.forEach((categoryName) => formData.append("Categories", categoryName));
    removedGalleryIds.forEach((galleryId) => formData.append("RemovedPostImages", galleryId));
    existingGallery.forEach((image) => { if (image.id) formData.append("ExistingPostImagesOrder", image.id); });
    newGalleryFiles.forEach((file) => formData.append("PostImages", file));

    try {
      await api.put(`/admin/posts/${postId}`, formData);
      navigate("/dashboard/posts");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || err.response?.data?.title || "Erreur lors de la mise à jour du post.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <p className="text-xl font-semibold text-slate-100">Chargement...</p>
      </div>
    );
  }

  const currentImageUrl = typeof postData.image === "string" ? getImageUrl(postData.thumbnail || postData.image) : null;

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-10 text-slate-100">
      <div className="mx-auto max-w-5xl">
        <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 text-white shadow-sm">
          <div className="p-8 md:p-10">
            <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-blue-100">
              Dashboard posts
            </p>
            <h1 className="mb-3 text-3xl font-bold md:text-4xl">Modifier un post</h1>
            <p className="max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
              Mettez à jour le contenu, l'image principale, la galerie et les catégories de votre article.
            </p>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-sm">
          {error && (
            <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>
          )}
          {submitting && (
            <div className="mb-6 flex items-center gap-3 rounded-xl border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm font-medium text-blue-300">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-400 border-t-transparent"></div>
              <span>Enregistrement du post en cours...</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
              <h2 className="mb-5 text-lg font-semibold text-white">Contenu principal</h2>
              <div className="space-y-6">
                <div>
                  <label htmlFor="title" className="mb-2 block text-sm font-medium text-slate-200">Titre</label>
                  <input type="text" id="title" name="title" value={postData.title} onChange={handleChange} required disabled={submitting}
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 focus:border-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label htmlFor="slug" className="mb-2 block text-sm font-medium text-slate-200">Slug</label>
                  <input type="text" id="slug" name="slug" value={postData.slug} onChange={handleChange} disabled={submitting}
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 focus:border-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label htmlFor="description" className="mb-2 block text-sm font-medium text-slate-200">Contenu</label>
                  <textarea id="description" name="description" value={postData.description} onChange={handleChange} required disabled={submitting} rows={8}
                    className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 focus:border-blue-500 focus:outline-none" />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
              <h2 className="mb-5 text-lg font-semibold text-white">Image principale</h2>
              {currentImageUrl && (
                <div className="mb-6">
                  <p className="mb-3 text-sm font-medium text-slate-300">Image actuelle</p>
                  <img src={currentImageUrl} alt="Post actuel" className="max-w-xs rounded-xl border border-slate-700 shadow" />
                </div>
              )}
              <input type="file" id="image" name="image" onChange={handleMainImageChange} accept="image/*" disabled={submitting}
                className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 file:mr-4 file:rounded-lg file:border-0 file:bg-white file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-950 hover:file:bg-slate-200" />
              {mainImagePreview && (
                <div className="mt-5">
                  <p className="mb-3 text-sm font-medium text-slate-300">Aperçu nouvelle image</p>
                  <img src={mainImagePreview} alt="Aperçu" className="max-w-xs rounded-xl border border-slate-700 shadow" />
                </div>
              )}
            </div>

            {existingGallery.length > 0 && (
              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
                <h2 className="mb-5 text-lg font-semibold text-white">Galerie existante</h2>
                <div className="flex flex-wrap gap-4">
                  {existingGallery.map((img, index) => (
                    <div key={`${img.id || img.path}-${index}`} className="relative rounded-xl border border-slate-700 bg-slate-900 p-3">
                      <span className="absolute left-2 top-2 rounded-lg bg-black/70 px-2 py-1 text-xs font-medium text-white">{index + 1}</span>
                      <img src={getImageUrl(img.thumbnail || img.path)} alt="Galerie" className="h-32 w-32 rounded-lg object-cover" />
                      <div className="mt-3 flex justify-center gap-2">
                        <button type="button" onClick={() => moveExistingGalleryImage(index, index - 1)} disabled={submitting || index === 0}
                          className="rounded-lg bg-slate-700 px-2 py-1 text-xs font-medium text-white transition hover:bg-slate-600 disabled:opacity-50">←</button>
                        <button type="button" onClick={() => moveExistingGalleryImage(index, index + 1)} disabled={submitting || index === existingGallery.length - 1}
                          className="rounded-lg bg-slate-700 px-2 py-1 text-xs font-medium text-white transition hover:bg-slate-600 disabled:opacity-50">→</button>
                        <button type="button" onClick={() => handleRemoveExisting(img)} disabled={submitting}
                          className="rounded-lg bg-red-600 px-2 py-1 text-xs font-medium text-white transition hover:bg-red-700">X</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
              <h2 className="mb-5 text-lg font-semibold text-white">Ajouter à la galerie</h2>
              <input type="file" id="gallery" name="gallery" multiple onChange={handleNewGalleryChange} accept="image/*" disabled={submitting}
                className="block w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 file:mr-4 file:rounded-lg file:border-0 file:bg-white file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-950 hover:file:bg-slate-200" />
              {newGalleryFiles.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-4">
                  {newGalleryFiles.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="relative rounded-xl border border-slate-700 bg-slate-900 p-3">
                      <span className="absolute left-2 top-2 rounded-lg bg-black/70 px-2 py-1 text-xs font-medium text-white">{index + 1}</span>
                      <img src={URL.createObjectURL(file)} alt="Aperçu" className="h-32 w-32 rounded-lg object-cover" />
                      <div className="mt-3 flex justify-center gap-2">
                        <button type="button" onClick={() => moveNewGalleryFile(index, index - 1)} disabled={submitting || index === 0}
                          className="rounded-lg bg-slate-700 px-2 py-1 text-xs font-medium text-white transition hover:bg-slate-600 disabled:opacity-50">←</button>
                        <button type="button" onClick={() => moveNewGalleryFile(index, index + 1)} disabled={submitting || index === newGalleryFiles.length - 1}
                          className="rounded-lg bg-slate-700 px-2 py-1 text-xs font-medium text-white transition hover:bg-slate-600 disabled:opacity-50">→</button>
                        <button type="button" onClick={() => handleRemoveNewFile(index)} disabled={submitting}
                          className="rounded-lg bg-red-600 px-2 py-1 text-xs font-medium text-white transition hover:bg-red-700">X</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
              <h2 className="mb-5 text-lg font-semibold text-white">Catégories</h2>
              <div className="flex flex-wrap gap-4">
                {allCategories.map((cat) => (
                  <label key={cat.id} className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm text-slate-300">
                    <input type="checkbox" value={cat.name} checked={selectedCategories.includes(cat.name)}
                      onChange={handleCategoryChange} disabled={submitting} className="accent-blue-500" />
                    <span>{cat.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button type="submit" disabled={submitting}
                className="w-full rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                {submitting ? "Enregistrement en cours..." : "Enregistrer"}
              </button>
              <button type="button" onClick={() => navigate("/dashboard/posts")} disabled={submitting}
                className="w-full rounded-xl border border-slate-700 px-6 py-3 font-semibold text-slate-200 transition hover:bg-slate-800 disabled:opacity-60 sm:w-auto">
                Annuler
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default EditPost;